<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { UFPMMember, UFPMFilter } from '../lib/types'

const props = defineProps<{
  filteredMembers:  UFPMMember[]
  totalCount:       number
  search:           string
  filters:          UFPMFilter
  uniqueCurrencies: string[]
  uniqueCategories: string[]
  dueMonthOptions:  Array<{ value: string; label: string }>
  selectedMemberId: string | null
  isUploading:      boolean
  uploadError:      string
  editingMember?:   UFPMMember | null
}>()

const emit = defineEmits<{
  'update:search':      [value: string]
  'update:filters':     [value: Partial<UFPMFilter>]
  'clearFilters':       []
  'upload':             [file: File]
  'selectMember':       [id: string]
  'deleteMember':       [id: string]
  'submitForm':         [data: Partial<UFPMMember>]
  'cancelEdit':         []
  'openAddMemberModal': []
  'viewProfile':        []
  'clearRoster':        []
}>()

// ---- Pagination ----

const PAGE_SIZE = 5
const currentPage = ref(0)

const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.filteredMembers.length / PAGE_SIZE))
)

const pagedMembers = computed(() => {
  const start = currentPage.value * PAGE_SIZE
  return props.filteredMembers.slice(start, start + PAGE_SIZE)
})

const pageStart = computed(() =>
  props.filteredMembers.length === 0 ? 0 : currentPage.value * PAGE_SIZE + 1
)

const pageEnd = computed(() =>
  Math.min((currentPage.value + 1) * PAGE_SIZE, props.filteredMembers.length)
)

// Reset to page 0 when filtered list changes (debounced to avoid mid-type jank)
let resetTimer: ReturnType<typeof setTimeout> | null = null
watch(
  () => props.filteredMembers,
  () => {
    if (resetTimer) clearTimeout(resetTimer)
    resetTimer = setTimeout(() => { currentPage.value = 0 }, 150)
  }
)

// Clamp page if a deletion shrinks the total below the current page
watch(totalPages, (pages) => {
  if (currentPage.value >= pages) currentPage.value = Math.max(0, pages - 1)
})

// When the selected member changes (deletion auto-select, external ufpmTools call),
// navigate to whichever page contains that member
watch(
  () => props.selectedMemberId,
  (id) => {
    if (!id) return
    const idx = props.filteredMembers.findIndex(m => m.id === id)
    if (idx === -1) return   // member filtered out — keep current page
    currentPage.value = Math.floor(idx / PAGE_SIZE)
  }
)

// ---- Search with debounce + clear button ----

const localSearch = ref(props.search)
let searchTimer: ReturnType<typeof setTimeout> | null = null

function onSearchInput(val: string) {
  localSearch.value = val
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => emit('update:search', val), 150)
}

watch(() => props.search, (v) => { localSearch.value = v })

// ---- Filters ----

const activeFilterCount = computed(() => {
  let count = 0
  if (props.filters.fitnessCurrency) count++
  if (props.filters.assessmentCategory) count++
  if (props.filters.dueMonth) count++
  return count
})

// ---- File upload ----

const fileInputEl = ref<HTMLInputElement | null>(null)

function handleFileChange(event: Event): void {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) emit('upload', file)
  if (fileInputEl.value) fileInputEl.value.value = ''
}
</script>

<template>
  <aside class="ufpm-sidebar" :class="{ 'ufpm-sidebar--uploading': isUploading }" aria-label="UFPM roster tools">
    <!-- Header -->
    <div class="ufpm-sidebar-head">
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
        <div class="acc-title" style="margin:0;">Roster</div>
        <div class="ufpm-count">
          <span>{{ filteredMembers.length }} / {{ totalCount }}</span> members
        </div>
      </div>

      <!-- Search -->
      <div class="field" style="margin-top:10px;">
        <label for="ufpm-search">Search Roster</label>
        <div class="ufpm-search-wrap">
          <input
            id="ufpm-search"
            type="text"
            placeholder="Name, rank, org, due date"
            autocomplete="off"
            :value="localSearch"
            @input="onSearchInput(($event.target as HTMLInputElement).value)"
          />
          <button
            v-if="localSearch"
            class="ufpm-search-clear"
            type="button"
            aria-label="Clear search"
            @click="onSearchInput('')"
          >✕</button>
        </div>
      </div>
    </div>

    <!-- Filters (collapsible) -->
    <details class="ufpm-filter-box" :open="false">
      <summary class="ufpm-filter-head" style="cursor:pointer; list-style:none; display:flex; align-items:center; justify-content:space-between;">
        <span style="display:flex;align-items:center;gap:6px;">
          <span class="ufpm-filter-chevron">▶</span>
          <span>
            Filters
            <span v-if="activeFilterCount > 0" style="color:var(--accent); font-weight:700;">({{ activeFilterCount }})</span>
            <span class="ufpm-filter-hint">— tap to expand</span>
          </span>
        </span>
        <button
          class="btn btn-secondary"
          type="button"
          @click.stop="emit('clearFilters')"
          style="font-size:10px; padding:4px 8px;"
        >
          Clear
        </button>
      </summary>

      <div class="field">
        <label for="ufpm-filter-currency">Fitness Currency</label>
        <select
          id="ufpm-filter-currency"
          :value="filters.fitnessCurrency"
          @change="emit('update:filters', { fitnessCurrency: ($event.target as HTMLSelectElement).value })"
        >
          <option value="">All currency statuses</option>
          <option v-for="c in uniqueCurrencies" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>
      <div class="field">
        <label for="ufpm-filter-category">Assessment Category</label>
        <select
          id="ufpm-filter-category"
          :value="filters.assessmentCategory"
          @change="emit('update:filters', { assessmentCategory: ($event.target as HTMLSelectElement).value })"
        >
          <option value="">All categories</option>
          <option v-for="c in uniqueCategories" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>
      <div class="field">
        <label for="ufpm-filter-due-month">Due Month / Year</label>
        <select
          id="ufpm-filter-due-month"
          :value="filters.dueMonth"
          @change="emit('update:filters', { dueMonth: ($event.target as HTMLSelectElement).value })"
        >
          <option value="">All due months</option>
          <option v-for="opt in dueMonthOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </div>
    </details>

    <!-- Upload -->
    <div class="ufpm-upload-box">
      <label>Upload Roster</label>
      <input
        ref="fileInputEl"
        type="file"
        accept=".csv,.tsv,.txt,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        style="display:none;"
        @change="handleFileChange"
      />
      <div class="ufpm-roster-actions">
        <button
          class="btn btn-primary"
          type="button"
          data-onboarding="ufpm-roster-import"
          :disabled="isUploading"
          @click="fileInputEl?.click()"
        >{{ isUploading ? 'Uploading…' : 'Upload Roster' }}</button>
        <button
          class="btn btn-danger"
          type="button"
          :disabled="isUploading || totalCount === 0"
          @click="emit('clearRoster')"
        >Clear Roster</button>
      </div>
      <Transition name="ufpm-roster-loading-fade">
      <div
        v-if="isUploading"
        class="ufpm-roster-loading"
        role="status"
        aria-live="polite"
        aria-label="Importing roster"
      >
        <div class="ufpm-roster-loading__header">
          <div class="ufpm-roster-loading__equalizer" aria-hidden="true">
            <span></span><span></span><span></span><span></span>
          </div>
          <div>
            <div class="ufpm-roster-loading__title">Importing roster</div>
            <div class="ufpm-roster-loading__subtitle">Parsing members and merging records…</div>
          </div>
        </div>
        <div class="ufpm-roster-loading__track" aria-hidden="true"><span></span></div>
        <div class="ufpm-roster-loading__steps" aria-hidden="true">
          <span>Reading file</span>
          <span>Matching members</span>
          <span>Updating roster</span>
        </div>
      </div>
      </Transition>
      <div v-if="uploadError" style="font-size:11px;color:var(--orange);margin-top:6px;">{{ uploadError }}</div>
    </div>

    <!-- Add member action -->
    <div style="margin-bottom: 12px;">
      <button
        class="btn btn-primary"
        type="button"
        style="width: 100%;"
        @click="emit('openAddMemberModal')"
      >
        + Add Member
      </button>
    </div>

    <!-- Roster list -->
    <div class="ufpm-roster-list">
      <p v-if="!filteredMembers.length && localSearch" class="ufpm-empty">
        No members match "{{ localSearch }}".
      </p>
      <p v-else-if="!filteredMembers.length" class="ufpm-empty">
        No members in roster.
      </p>
      <div
        v-for="(member, index) in pagedMembers"
        :key="member.id"
        class="ufpm-member-row"
        :class="{
          active: member.id === selectedMemberId,
          'search-highlight': localSearch && index === 0
        }"
      >
        <div class="ufpm-member-row-top">
          <button
            class="ufpm-member-select"
            type="button"
            @click="emit('selectMember', member.id)"
          >
            <span class="ufpm-member-name">{{ member.fullName || 'Unnamed Member' }}</span>
            <span class="ufpm-member-meta">{{ [member.rank, member.organization].filter(Boolean).join(' | ') || 'No unit data' }}</span>
            <span class="ufpm-member-status">{{ member.fitnessCurrency || 'Currency unknown' }}</span>
          </button>
          <button
            class="ufpm-member-delete"
            type="button"
            :title="'Delete ' + (member.fullName || 'member')"
            :aria-label="'Delete ' + (member.fullName || 'member')"
            @click="emit('deleteMember', member.id)"
          >X</button>
        </div>
        <button
          v-if="member.id === selectedMemberId"
          class="ufpm-view-profile-btn"
          type="button"
          @click.stop="emit('viewProfile')"
        >
          View Profile →
        </button>
      </div>
    </div>

    <!-- Pagination controls -->
    <div v-if="totalPages > 1" class="ufpm-pagination">
      <button
        class="btn btn-secondary ufpm-page-btn"
        type="button"
        :disabled="currentPage === 0"
        @click="currentPage--"
      >‹ Prev</button>
      <span class="ufpm-page-indicator">{{ pageStart }}–{{ pageEnd }} of {{ filteredMembers.length }}</span>
      <button
        class="btn btn-secondary ufpm-page-btn"
        type="button"
        :disabled="currentPage >= totalPages - 1"
        @click="currentPage++"
      >Next ›</button>
    </div>
  </aside>
</template>
